import React, { useEffect, useState, useMemo } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Plus, Check, X, MagnifyingGlass, PencilSimple, Trash, Clock, User, Eye, FunnelSimple, ChartBar, ClipboardText } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Timesheets = ({ user }) => {
  const [timesheets, setTimesheets] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [contractorTimesheets, setContractorTimesheets] = useState([]);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [editingContractor, setEditingContractor] = useState(null);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [hoursState, setHoursState] = useState({
    monday: 0, tuesday: 0, wednesday: 0, thursday: 0, friday: 0, saturday: 0, sunday: 0
  });
  
  // Filters for timesheets
  const [filterContractor, setFilterContractor] = useState('');
  const [filterWeek, setFilterWeek] = useState('');

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [timesheetsRes, contractorsRes, dashboardRes] = await Promise.all([
        api.get('/timesheets'),
        api.get('/timesheets/contractors'),
        api.get('/timesheets/dashboard')
      ]);
      setTimesheets(timesheetsRes.data);
      setContractors(contractorsRes.data);
      setDashboardStats(dashboardRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get unique weeks from timesheets for filter dropdown
  const uniqueWeeks = useMemo(() => {
    const weeks = [...new Set(timesheets.map(t => t.week_ending))];
    return weeks.sort().reverse();
  }, [timesheets]);

  // Filtered timesheets
  const filteredTimesheets = useMemo(() => {
    return timesheets.filter(t => {
      const matchesContractor = !filterContractor || t.contractor_id === filterContractor;
      const matchesWeek = !filterWeek || t.week_ending === filterWeek;
      return matchesContractor && matchesWeek;
    });
  }, [timesheets, filterContractor, filterWeek]);

  const searchContractors = async (term) => {
    try {
      const response = await api.get(`/timesheets/contractors?search=${encodeURIComponent(term)}`);
      setContractors(response.data);
    } catch (error) {
      toast.error('Failed to search contractors');
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length > 2) {
      searchContractors(value);
    } else if (value.length === 0) {
      loadData();
    }
  };

  // Contractor functions
  const handleCreateContractor = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await api.post('/timesheets/contractors', {
        full_name: formData.get('full_name'),
        telephone: formData.get('telephone'),
        daily_rate: parseFloat(formData.get('daily_rate')),
        account_number: formData.get('account_number'),
        account_name: formData.get('account_name'),
        bank_name: formData.get('bank_name'),
      });
      toast.success('Contractor created successfully');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contractor');
    }
  };

  const handleUpdateContractor = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await api.put(`/timesheets/contractors/${editingContractor.id}`, {
        full_name: formData.get('full_name'),
        telephone: formData.get('telephone'),
        daily_rate: parseFloat(formData.get('daily_rate')),
        account_number: formData.get('account_number'),
        account_name: formData.get('account_name'),
        bank_name: formData.get('bank_name'),
      });
      toast.success('Contractor updated successfully');
      setOpenDialog(null);
      setEditingContractor(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contractor');
    }
  };

  const handleDeleteContractor = async (contractorId) => {
    if (!window.confirm('Are you sure you want to delete this contractor?')) return;
    
    try {
      await api.delete(`/timesheets/contractors/${contractorId}`);
      toast.success('Contractor deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete contractor');
    }
  };

  const viewContractorDetails = async (contractor) => {
    setSelectedContractor(contractor);
    try {
      const response = await api.get(`/timesheets/contractors/${contractor.id}/timesheets`);
      setContractorTimesheets(response.data);
    } catch (error) {
      toast.error('Failed to load contractor timesheets');
    }
    setOpenDialog('contractorDetails');
  };

  // Timesheet functions
  const resetHoursState = () => {
    setHoursState({
      monday: 0, tuesday: 0, wednesday: 0, thursday: 0, friday: 0, saturday: 0, sunday: 0
    });
  };

  const calculateTotalHours = () => {
    return Object.values(hoursState).reduce((sum, h) => sum + (parseFloat(h) || 0), 0);
  };

  const handleHoursChange = (day, value) => {
    setHoursState(prev => ({ ...prev, [day]: parseFloat(value) || 0 }));
  };

  const handleCreateTimesheet = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await api.post('/timesheets', {
        contractor_id: formData.get('contractor_id'),
        contractor_name: '', // Will be set by backend
        week_ending: formData.get('week_ending'),
        hours: hoursState,
        total_hours: calculateTotalHours(),
        notes: formData.get('notes') || null,
      });
      toast.success('Timesheet created successfully');
      setOpenDialog(null);
      resetHoursState();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create timesheet');
    }
  };

  const handleCreateTimesheetForContractor = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await api.post('/timesheets', {
        contractor_id: selectedContractor.id,
        contractor_name: selectedContractor.full_name,
        week_ending: formData.get('week_ending'),
        hours: hoursState,
        total_hours: calculateTotalHours(),
        notes: formData.get('notes') || null,
      });
      toast.success('Timesheet created successfully');
      setOpenDialog(null);
      resetHoursState();
      loadData();
      // Refresh contractor timesheets
      const response = await api.get(`/timesheets/contractors/${selectedContractor.id}/timesheets`);
      setContractorTimesheets(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create timesheet');
    }
  };

  const handleUpdateTimesheet = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await api.put(`/timesheets/${editingTimesheet.id}`, {
        week_ending: formData.get('week_ending'),
        hours: hoursState,
        total_hours: calculateTotalHours(),
        notes: formData.get('notes') || null,
      });
      toast.success('Timesheet updated successfully');
      setOpenDialog(null);
      setEditingTimesheet(null);
      resetHoursState();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update timesheet');
    }
  };

  const handleEditTimesheet = (timesheet) => {
    setEditingTimesheet(timesheet);
    setHoursState(timesheet.hours);
    setOpenDialog('editTimesheet');
  };

  const handleViewTimesheet = (timesheet) => {
    setSelectedTimesheet(timesheet);
    setOpenDialog('timesheetDetails');
  };

  const handleApprove = async (timesheetId) => {
    try {
      await api.put(`/timesheets/${timesheetId}/approve`);
      toast.success('Timesheet approved - notification sent to accounts');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve timesheet');
    }
  };

  const handleReject = async (timesheetId) => {
    try {
      await api.put(`/timesheets/${timesheetId}/reject`);
      toast.success('Timesheet rejected');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject timesheet');
    }
  };

  const handleDeleteTimesheet = async (timesheetId) => {
    if (!window.confirm('Are you sure you want to delete this timesheet?')) return;
    
    try {
      await api.delete(`/timesheets/${timesheetId}`);
      toast.success('Timesheet deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete timesheet');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-emerald-100 text-emerald-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-amber-100 text-amber-800';
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (!isAdmin) {
    return (
      <div className="text-center py-8">
        <p className="text-[#505631]">Only administrators can access this module.</p>
      </div>
    );
  }

  return (
    <div data-testid="timesheets-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#2B3210] mb-2">
          Timesheets
        </h1>
        <p className="text-base text-[#505631]">Manage contractor timesheets and approvals</p>
      </div>

      <Tabs defaultValue="dashboard" className="space-y-6">
        <TabsList className="bg-[#FBF8EF] border border-[#E5E2D9]">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-white" data-testid="tab-dashboard">
            <ChartBar size={16} className="mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="timesheets" className="data-[state=active]:bg-white">
            <ClipboardText size={16} className="mr-2" />
            Timesheets
          </TabsTrigger>
          <TabsTrigger value="contractors" className="data-[state=active]:bg-white">
            <User size={16} className="mr-2" />
            Contractors
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Total Timesheets</p>
              <p className="text-3xl font-bold text-[#2B3210]">{dashboardStats?.total_timesheets || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Pending Approval</p>
              <p className="text-3xl font-bold text-amber-600">{dashboardStats?.pending_timesheets || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Approved</p>
              <p className="text-3xl font-bold text-green-600">{dashboardStats?.approved_timesheets || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Rejected</p>
              <p className="text-3xl font-bold text-red-600">{dashboardStats?.rejected_timesheets || 0}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Total Hours Logged</p>
              <p className="text-2xl font-bold text-[#B2560D]">{(dashboardStats?.total_hours || 0).toLocaleString()}</p>
              <p className="text-xs text-[#505631] mt-1">All time</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Hours This Month</p>
              <p className="text-2xl font-bold text-blue-600">{(dashboardStats?.month_hours || 0).toLocaleString()}</p>
              <p className="text-xs text-[#505631] mt-1">Current month</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Active Employees</p>
              <p className="text-2xl font-bold text-[#2B3210]">{dashboardStats?.unique_employees || 0}</p>
              <p className="text-xs text-[#505631] mt-1">With timesheets</p>
            </div>
          </div>
        </TabsContent>

        {/* Timesheets Tab */}
        <TabsContent value="timesheets">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h2 className="text-xl font-medium text-[#2B3210]">Weekly Timesheets</h2>
              <div className="flex flex-wrap gap-3 items-center">
                {/* Filter by Contractor */}
                <div className="flex items-center gap-2">
                  <FunnelSimple size={18} className="text-[#505631]" />
                  <select
                    value={filterContractor}
                    onChange={(e) => setFilterContractor(e.target.value)}
                    data-testid="filter-contractor-select"
                    className="px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                  >
                    <option value="">All Contractors</option>
                    {contractors.map((c) => (
                      <option key={c.id} value={c.id}>{c.full_name}</option>
                    ))}
                  </select>
                </div>
                {/* Filter by Week */}
                <select
                  value={filterWeek}
                  onChange={(e) => setFilterWeek(e.target.value)}
                  data-testid="filter-week-select"
                  className="px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                >
                  <option value="">All Weeks</option>
                  {uniqueWeeks.map((week) => (
                    <option key={week} value={week}>{week}</option>
                  ))}
                </select>
                {(filterContractor || filterWeek) && (
                  <button
                    onClick={() => { setFilterContractor(''); setFilterWeek(''); }}
                    className="text-sm text-[#B2560D] hover:underline"
                  >
                    Clear filters
                  </button>
                )}
                <Dialog open={openDialog === 'createTimesheet'} onOpenChange={(open) => {
                  setOpenDialog(open ? 'createTimesheet' : null);
                  if (!open) resetHoursState();
                }}>
                  <DialogTrigger asChild>
                    <button className="btn-primary flex items-center gap-2" data-testid="create-timesheet-button">
                      <Plus size={18} />
                      New Timesheet
                    </button>
                  </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create Timesheet</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateTimesheet} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Select Contractor</label>
                      <select
                        name="contractor_id"
                        data-testid="timesheet-contractor-select"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                        required
                      >
                        <option value="">Choose a contractor...</option>
                        {contractors.map((c) => (
                          <option key={c.id} value={c.id}>{c.full_name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Week Ending</label>
                      <input
                        type="date"
                        name="week_ending"
                        data-testid="timesheet-week-ending-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-2">Hours by Day</label>
                      <div className="grid grid-cols-7 gap-2">
                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                          <div key={day}>
                            <label className="block text-xs text-[#505631] mb-1 capitalize">{day.slice(0, 3)}</label>
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              max="24"
                              value={hoursState[day]}
                              onChange={(e) => handleHoursChange(day, e.target.value)}
                              data-testid={`timesheet-${day}-input`}
                              className="w-full px-2 py-2 border border-[#E5E2D9] rounded-lg text-sm text-center"
                            />
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 p-3 bg-[#FBF8EF] rounded-lg">
                        <p className="text-sm font-medium text-[#2B3210]">
                          Total Hours: <span className="text-[#B2560D]">{calculateTotalHours()}h</span>
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Notes (Optional)</label>
                      <textarea
                        name="notes"
                        data-testid="timesheet-notes-input"
                        rows="2"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                        placeholder="Any additional notes..."
                      />
                    </div>
                    <button type="submit" className="btn-primary w-full" data-testid="submit-timesheet-button">
                      Create Timesheet
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Contractor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Week Ending</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Total Hours</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Created By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {filteredTimesheets.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-[#505631]">
                        {timesheets.length === 0 ? 'No timesheets found. Create one to get started.' : 'No timesheets match the current filters.'}
                      </td>
                    </tr>
                  ) : (
                    filteredTimesheets.map((timesheet) => (
                      <tr 
                        key={timesheet.id} 
                        data-testid={`timesheet-row-${timesheet.id}`}
                        className="cursor-pointer hover:bg-[#FBF8EF] transition-colors"
                        onClick={() => handleViewTimesheet(timesheet)}
                      >
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-[#2B3210]">{timesheet.contractor_name}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#2B3210]">{timesheet.week_ending}</td>
                        <td className="px-6 py-4 text-sm text-[#2B3210] font-medium">{timesheet.total_hours}h</td>
                        <td className="px-6 py-4 text-sm text-[#505631]">{timesheet.created_by_name || 'N/A'}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(timesheet.status)}`}>
                            {timesheet.status}
                          </span>
                        </td>
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-2">
                            {/* Edit - only for pending timesheets */}
                            {timesheet.status === 'pending' && (
                              <button
                                onClick={() => handleEditTimesheet(timesheet)}
                                data-testid={`edit-timesheet-${timesheet.id}`}
                                className="p-2 text-[#B2560D] hover:bg-orange-50 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <PencilSimple size={18} />
                              </button>
                            )}
                            {/* Approve/Reject - only for pending timesheets and different admin */}
                            {timesheet.status === 'pending' && timesheet.created_by !== user.id && (
                              <>
                                <button
                                  onClick={() => handleApprove(timesheet.id)}
                                  data-testid={`approve-timesheet-${timesheet.id}`}
                                  className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                  title="Approve"
                                >
                                  <Check size={18} />
                                </button>
                                <button
                                  onClick={() => handleReject(timesheet.id)}
                                  data-testid={`reject-timesheet-${timesheet.id}`}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Reject"
                                >
                                  <X size={18} />
                                </button>
                              </>
                            )}
                            {/* Delete - only for pending timesheets */}
                            {timesheet.status === 'pending' && (
                              <button
                                onClick={() => handleDeleteTimesheet(timesheet.id)}
                                data-testid={`delete-timesheet-${timesheet.id}`}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash size={18} />
                              </button>
                            )}
                            {/* Show approved info */}
                            {timesheet.status === 'approved' && (
                              <span className="text-xs text-[#505631] py-2">
                                Approved by {timesheet.approved_by_name}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Reports Section */}
          <div className="mt-6 bg-white rounded-xl border border-[#E5E2D9] p-6">
            <h2 className="text-xl font-medium text-[#2B3210] mb-4">Reports</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 bg-[#FBF8EF] rounded-lg">
                <p className="text-sm text-[#505631] mb-1">Total Timesheets</p>
                <p className="text-2xl font-semibold text-[#2B3210]">{timesheets.length}</p>
              </div>
              <div className="p-4 bg-[#FBF8EF] rounded-lg">
                <p className="text-sm text-[#505631] mb-1">Pending Approval</p>
                <p className="text-2xl font-semibold text-amber-600">
                  {timesheets.filter((t) => t.status === 'pending').length}
                </p>
              </div>
              <div className="p-4 bg-[#FBF8EF] rounded-lg">
                <p className="text-sm text-[#505631] mb-1">Approved</p>
                <p className="text-2xl font-semibold text-emerald-600">
                  {timesheets.filter((t) => t.status === 'approved').length}
                </p>
              </div>
              <div className="p-4 bg-[#FBF8EF] rounded-lg">
                <p className="text-sm text-[#505631] mb-1">Total Hours (Approved)</p>
                <p className="text-2xl font-semibold text-[#2B3210]">
                  {timesheets.filter((t) => t.status === 'approved').reduce((sum, t) => sum + t.total_hours, 0)}h
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Contractors Tab */}
        <TabsContent value="contractors">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h2 className="text-xl font-medium text-[#2B3210]">Contractor Records</h2>
              <div className="flex gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-none">
                  <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#505631]" size={18} />
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={searchTerm}
                    onChange={handleSearch}
                    data-testid="contractor-search-input"
                    className="pl-10 pr-4 py-2 border border-[#E5E2D9] rounded-lg w-full sm:w-64 focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                  />
                </div>
                <Dialog open={openDialog === 'createContractor'} onOpenChange={(open) => setOpenDialog(open ? 'createContractor' : null)}>
                  <DialogTrigger asChild>
                    <button className="btn-primary flex items-center gap-2 whitespace-nowrap" data-testid="create-contractor-button">
                      <Plus size={18} />
                      New Contractor
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Add New Contractor</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateContractor} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Full Name</label>
                        <input
                          type="text"
                          name="full_name"
                          data-testid="contractor-full-name-input"
                          className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Telephone Number</label>
                        <input
                          type="tel"
                          name="telephone"
                          data-testid="contractor-telephone-input"
                          className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Daily Rate (₦)</label>
                        <input
                          type="number"
                          name="daily_rate"
                          step="0.01"
                          min="0"
                          data-testid="contractor-daily-rate-input"
                          className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                          placeholder="e.g., 15000"
                          required
                        />
                      </div>
                      <div className="border-t border-[#E5E2D9] pt-4">
                        <h3 className="text-sm font-semibold text-[#2B3210] mb-3">Bank Account Details</h3>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Account Number</label>
                            <input
                              type="text"
                              name="account_number"
                              data-testid="contractor-account-number-input"
                              className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Account Name</label>
                            <input
                              type="text"
                              name="account_name"
                              data-testid="contractor-account-name-input"
                              className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Bank Name</label>
                            <input
                              type="text"
                              name="bank_name"
                              data-testid="contractor-bank-name-input"
                              className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                              required
                            />
                          </div>
                        </div>
                      </div>
                      <button type="submit" className="btn-primary w-full" data-testid="submit-contractor-button">
                        Create Contractor
                      </button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Telephone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Daily Rate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Bank Details</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {contractors.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-[#505631]">
                        No contractors found. Add one to get started.
                      </td>
                    </tr>
                  ) : (
                    contractors.map((contractor) => (
                      <tr 
                        key={contractor.id} 
                        data-testid={`contractor-row-${contractor.id}`}
                        className="cursor-pointer hover:bg-[#FBF8EF] transition-colors"
                        onClick={() => viewContractorDetails(contractor)}
                      >
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-[#2B3210]">{contractor.full_name}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#2B3210]">{contractor.telephone}</td>
                        <td className="px-6 py-4 text-sm font-medium text-[#B2560D]">
                          ₦{contractor.daily_rate?.toLocaleString() || '-'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-[#2B3210]">{contractor.account_number}</div>
                          <div className="text-xs text-[#505631]">{contractor.bank_name}</div>
                        </td>
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-2">
                            <button
                              onClick={() => viewContractorDetails(contractor)}
                              data-testid={`view-contractor-${contractor.id}`}
                              className="p-2 text-[#505631] hover:bg-[#E5E2D9] rounded-lg transition-colors"
                              title="View Details & History"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => {
                                setEditingContractor(contractor);
                                setOpenDialog('editContractor');
                              }}
                              data-testid={`edit-contractor-${contractor.id}`}
                              className="p-2 text-[#B2560D] hover:bg-orange-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <PencilSimple size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteContractor(contractor.id)}
                              data-testid={`delete-contractor-${contractor.id}`}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Timesheet Dialog */}
      <Dialog open={openDialog === 'editTimesheet'} onOpenChange={(open) => {
        setOpenDialog(open ? 'editTimesheet' : null);
        if (!open) {
          setEditingTimesheet(null);
          resetHoursState();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Timesheet</DialogTitle>
          </DialogHeader>
          {editingTimesheet && (
            <form onSubmit={handleUpdateTimesheet} className="space-y-4">
              <div className="p-3 bg-[#FBF8EF] rounded-lg">
                <p className="text-sm text-[#505631]">Contractor: <span className="font-medium text-[#2B3210]">{editingTimesheet.contractor_name}</span></p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Week Ending</label>
                <input
                  type="date"
                  name="week_ending"
                  defaultValue={editingTimesheet.week_ending}
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-2">Hours by Day</label>
                <div className="grid grid-cols-7 gap-2">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                    <div key={day}>
                      <label className="block text-xs text-[#505631] mb-1 capitalize">{day.slice(0, 3)}</label>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="24"
                        value={hoursState[day]}
                        onChange={(e) => handleHoursChange(day, e.target.value)}
                        className="w-full px-2 py-2 border border-[#E5E2D9] rounded-lg text-sm text-center"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-3 bg-[#FBF8EF] rounded-lg">
                  <p className="text-sm font-medium text-[#2B3210]">
                    Total Hours: <span className="text-[#B2560D]">{calculateTotalHours()}h</span>
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Notes (Optional)</label>
                <textarea
                  name="notes"
                  rows="2"
                  defaultValue={editingTimesheet.notes || ''}
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setOpenDialog(null);
                    setEditingTimesheet(null);
                    resetHoursState();
                  }}
                  className="flex-1 px-4 py-2.5 border border-[#E5E2D9] text-[#505631] rounded-lg hover:bg-[#FBF8EF]"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Contractor Dialog */}
      <Dialog open={openDialog === 'editContractor'} onOpenChange={(open) => {
        setOpenDialog(open ? 'editContractor' : null);
        if (!open) setEditingContractor(null);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Contractor</DialogTitle>
          </DialogHeader>
          {editingContractor && (
            <form onSubmit={handleUpdateContractor} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  defaultValue={editingContractor.full_name}
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Telephone Number</label>
                <input
                  type="tel"
                  name="telephone"
                  defaultValue={editingContractor.telephone}
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Daily Rate (₦)</label>
                <input
                  type="number"
                  name="daily_rate"
                  step="0.01"
                  min="0"
                  defaultValue={editingContractor.daily_rate || ''}
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                  placeholder="e.g., 15000"
                  required
                />
              </div>
              <div className="border-t border-[#E5E2D9] pt-4">
                <h3 className="text-sm font-semibold text-[#2B3210] mb-3">Bank Account Details</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Account Number</label>
                    <input
                      type="text"
                      name="account_number"
                      defaultValue={editingContractor.account_number}
                      className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Account Name</label>
                    <input
                      type="text"
                      name="account_name"
                      defaultValue={editingContractor.account_name}
                      className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Bank Name</label>
                    <input
                      type="text"
                      name="bank_name"
                      defaultValue={editingContractor.bank_name}
                      className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setOpenDialog(null);
                    setEditingContractor(null);
                  }}
                  className="flex-1 px-4 py-2.5 border border-[#E5E2D9] text-[#505631] rounded-lg hover:bg-[#FBF8EF]"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Contractor Details Dialog */}
      <Dialog open={openDialog === 'contractorDetails'} onOpenChange={(open) => {
        setOpenDialog(open ? 'contractorDetails' : null);
        if (!open) {
          setSelectedContractor(null);
          setContractorTimesheets([]);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contractor Details & History</DialogTitle>
          </DialogHeader>
          {selectedContractor && (
            <div className="space-y-6">
              {/* Contractor Info */}
              <div className="bg-[#FBF8EF] p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-[#2B3210] mb-3">{selectedContractor.full_name}</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#505631]">Telephone</p>
                    <p className="font-medium text-[#2B3210]">{selectedContractor.telephone}</p>
                  </div>
                  <div>
                    <p className="text-[#505631]">Daily Rate</p>
                    <p className="font-medium text-[#B2560D]">₦{selectedContractor.daily_rate?.toLocaleString() || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-[#505631]">Bank</p>
                    <p className="font-medium text-[#2B3210]">{selectedContractor.bank_name}</p>
                  </div>
                  <div>
                    <p className="text-[#505631]">Account Number</p>
                    <p className="font-medium text-[#2B3210]">{selectedContractor.account_number}</p>
                  </div>
                  <div>
                    <p className="text-[#505631]">Account Name</p>
                    <p className="font-medium text-[#2B3210]">{selectedContractor.account_name}</p>
                  </div>
                </div>
              </div>

              {/* Create Timesheet Button */}
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    resetHoursState();
                    setOpenDialog('createTimesheetForContractor');
                  }}
                  className="btn-primary flex items-center gap-2"
                  data-testid="create-timesheet-for-contractor-button"
                >
                  <Plus size={18} />
                  Create Timesheet for {selectedContractor.full_name.split(' ')[0]}
                </button>
              </div>

              {/* Timesheet History */}
              <div>
                <h3 className="text-lg font-semibold text-[#2B3210] mb-3">Timesheet History</h3>
                {contractorTimesheets.length === 0 ? (
                  <p className="text-sm text-[#505631] text-center py-4">No timesheets found for this contractor.</p>
                ) : (
                  <div className="space-y-2">
                    {contractorTimesheets.map((ts) => (
                      <div 
                        key={ts.id} 
                        className="flex items-center justify-between p-3 bg-white border border-[#E5E2D9] rounded-lg"
                      >
                        <div>
                          <p className="text-sm font-medium text-[#2B3210]">Week ending: {ts.week_ending}</p>
                          <p className="text-xs text-[#505631]">{ts.total_hours} hours | Created by {ts.created_by_name}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ts.status)}`}>
                          {ts.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Timesheet for Specific Contractor Dialog */}
      <Dialog open={openDialog === 'createTimesheetForContractor'} onOpenChange={(open) => {
        setOpenDialog(open ? 'createTimesheetForContractor' : null);
        if (!open) resetHoursState();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Timesheet for {selectedContractor?.full_name}</DialogTitle>
          </DialogHeader>
          {selectedContractor && (
            <form onSubmit={handleCreateTimesheetForContractor} className="space-y-4">
              <div className="p-3 bg-[#FBF8EF] rounded-lg">
                <p className="text-sm text-[#505631]">Contractor: <span className="font-medium text-[#2B3210]">{selectedContractor.full_name}</span></p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Week Ending</label>
                <input
                  type="date"
                  name="week_ending"
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-2">Hours by Day</label>
                <div className="grid grid-cols-7 gap-2">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                    <div key={day}>
                      <label className="block text-xs text-[#505631] mb-1 capitalize">{day.slice(0, 3)}</label>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="24"
                        value={hoursState[day]}
                        onChange={(e) => handleHoursChange(day, e.target.value)}
                        className="w-full px-2 py-2 border border-[#E5E2D9] rounded-lg text-sm text-center"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-3 bg-[#FBF8EF] rounded-lg">
                  <p className="text-sm font-medium text-[#2B3210]">
                    Total Hours: <span className="text-[#B2560D]">{calculateTotalHours()}h</span>
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Notes (Optional)</label>
                <textarea
                  name="notes"
                  rows="2"
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                  placeholder="Any additional notes..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setOpenDialog('contractorDetails')}
                  className="flex-1 px-4 py-2.5 border border-[#E5E2D9] text-[#505631] rounded-lg hover:bg-[#FBF8EF]"
                >
                  Back
                </button>
                <button type="submit" className="flex-1 btn-primary">
                  Create Timesheet
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Timesheet Details Dialog */}
      <Dialog open={openDialog === 'timesheetDetails'} onOpenChange={(open) => {
        setOpenDialog(open ? 'timesheetDetails' : null);
        if (!open) setSelectedTimesheet(null);
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Timesheet Details</DialogTitle>
          </DialogHeader>
          {selectedTimesheet && (
            <div className="space-y-6">
              {/* Status Banner */}
              <div className={`p-4 rounded-lg text-center ${
                selectedTimesheet.status === 'approved' ? 'bg-emerald-50 text-emerald-800' :
                selectedTimesheet.status === 'rejected' ? 'bg-red-50 text-red-800' :
                'bg-amber-50 text-amber-800'
              }`}>
                <span className="font-semibold uppercase">{selectedTimesheet.status}</span>
                {selectedTimesheet.status === 'approved' && selectedTimesheet.approved_by_name && (
                  <span className="block text-sm mt-1">Approved by {selectedTimesheet.approved_by_name}</span>
                )}
              </div>

              {/* Contractor & Week Info */}
              <div className="bg-[#FBF8EF] p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-[#505631]">Contractor</p>
                    <p className="text-lg font-semibold text-[#2B3210]">{selectedTimesheet.contractor_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#505631]">Week Ending</p>
                    <p className="text-lg font-semibold text-[#2B3210]">{selectedTimesheet.week_ending}</p>
                  </div>
                </div>
              </div>

              {/* Hours Breakdown */}
              <div>
                <h3 className="text-sm font-semibold text-[#2B3210] mb-3">Hours Breakdown</h3>
                <div className="grid grid-cols-7 gap-2 text-center">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => {
                    const dayKey = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][i];
                    const hours = selectedTimesheet.hours?.[dayKey] || 0;
                    return (
                      <div key={day} className="bg-[#FBF8EF] p-3 rounded-lg">
                        <p className="text-xs text-[#505631] mb-1">{day}</p>
                        <p className={`text-lg font-semibold ${hours > 0 ? 'text-[#2B3210]' : 'text-[#E5E2D9]'}`}>
                          {hours}h
                        </p>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 p-4 bg-[#B2560D] text-white rounded-lg text-center">
                  <p className="text-sm">Total Hours</p>
                  <p className="text-3xl font-bold">{selectedTimesheet.total_hours}h</p>
                </div>
              </div>

              {/* Notes */}
              {selectedTimesheet.notes && (
                <div>
                  <h3 className="text-sm font-semibold text-[#2B3210] mb-2">Notes</h3>
                  <p className="text-sm text-[#505631] bg-[#FBF8EF] p-3 rounded-lg">{selectedTimesheet.notes}</p>
                </div>
              )}

              {/* Metadata */}
              <div className="border-t border-[#E5E2D9] pt-4 text-sm text-[#505631]">
                <div className="grid grid-cols-2 gap-2">
                  <p>Created by: <span className="text-[#2B3210] font-medium">{selectedTimesheet.created_by_name || 'N/A'}</span></p>
                  <p>Created: <span className="text-[#2B3210] font-medium">{selectedTimesheet.created_at ? new Date(selectedTimesheet.created_at).toLocaleString() : 'N/A'}</span></p>
                  {selectedTimesheet.approved_at && (
                    <>
                      <p>Approved by: <span className="text-[#2B3210] font-medium">{selectedTimesheet.approved_by_name}</span></p>
                      <p>Approved: <span className="text-[#2B3210] font-medium">{new Date(selectedTimesheet.approved_at).toLocaleString()}</span></p>
                    </>
                  )}
                </div>
              </div>

              {/* Actions */}
              {selectedTimesheet.status === 'pending' && (
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      handleEditTimesheet(selectedTimesheet);
                    }}
                    className="flex-1 px-4 py-2.5 border border-[#B2560D] text-[#B2560D] rounded-lg hover:bg-orange-50 flex items-center justify-center gap-2"
                  >
                    <PencilSimple size={18} />
                    Edit Timesheet
                  </button>
                  {selectedTimesheet.created_by !== user.id && (
                    <button
                      onClick={() => {
                        handleApprove(selectedTimesheet.id);
                        setOpenDialog(null);
                      }}
                      className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center justify-center gap-2"
                    >
                      <Check size={18} />
                      Approve
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Timesheets;
